import React from 'react';
import FieldTextValue from './FieldTextValue.jsx';
import { Modal } from '../ui-components/Modal.jsx';
import SpeakerHeadshot from '../ui-components/SpeakerHeadshot.jsx';
import ExpandableBio from '../ui-components/ExpandableBio.jsx';
import { Clock, MapPin } from 'lucide-react';
import { FIXED_SPEAKER_FIELD_IDS } from '../constants.js';
import { getFormattedSpeakerName, isDisplayableRange } from '../utils/helpers.js';
import { normalizeUrl } from './normalize-url.js';
import { useI18n } from '../i18n/index.jsx';

const SOCIAL_FIELD_IDS = ['linkedin_url', 'twitter_url', 'facebook_url', 'website', 'email'];
const NAME_FIELD_IDS = ['salutation', 'honorific'];

// Social Media Icons — defined before SOCIAL_ICON_MAP to avoid TDZ errors in the bundle
const TwitterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
    <path
      d="M25.9986 16.0004C25.9986 21.051 22.2549 25.2264 17.3915 25.904C16.9368 25.9671 16.4716 26 15.9993 26C15.4542 26 14.9189 25.9566 14.3976 25.8725C9.63649 25.1059 6 20.9774 6 16.0004C6 10.4775 10.4773 6 16 6C21.5227 6 26 10.4775 26 16.0004H25.9986Z"
      fill="#1C1C1B"
    />
    <path
      d="M10.0556 10.4102L14.6674 16.5763L10.0269 21.5898H11.0716L15.1348 17.2006L18.4174 21.5898H21.9719L17.1008 15.0768L21.4205 10.4102H20.3758L16.6342 14.4525L13.6108 10.4102H10.0563H10.0556ZM11.5915 11.1795H13.224L20.4347 20.8204H18.8021L11.5915 11.1795Z"
      fill="white"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="10" fill="#1877F2" />
    <path
      d="M13.8926 12.8906L14.3359 10H11.5625V8.125C11.5625 7.33398 11.9492 6.5625 13.1914 6.5625H14.4531V4.10156C14.4531 4.10156 13.3086 3.90625 12.2148 3.90625C9.92969 3.90625 8.4375 5.29102 8.4375 7.79688V10H5.89844V12.8906H8.4375V19.8789C8.94727 19.959 9.46875 20 10 20C10.5312 20 11.0527 19.959 11.5625 19.8789V12.8906H13.8926Z"
      fill="white"
    />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="10" fill="#0A66C2" />
    <path
      d="M14.688 4.5H5.31201C5.09665 4.5 4.89012 4.58555 4.73783 4.73783C4.58555 4.89012 4.5 5.09665 4.5 5.31201V14.688C4.5 14.9033 4.58555 15.1099 4.73783 15.2622C4.89012 15.4144 5.09665 15.5 5.31201 15.5H14.688C14.9033 15.5 15.1099 15.4144 15.2622 15.2622C15.4144 15.1099 15.5 14.9033 15.5 14.688V5.31201C15.5 5.09665 15.4144 4.89012 15.2622 4.73783C15.1099 4.58555 14.9033 4.5 14.688 4.5ZM7.77861 13.8706H6.12479V8.61736H7.77861V13.8706ZM6.95056 7.88938C6.76296 7.88832 6.57988 7.83171 6.42442 7.72671C6.26896 7.6217 6.14809 7.47299 6.07707 7.29936C6.00604 7.12572 5.98805 6.93494 6.02535 6.75108C6.06265 6.56723 6.15358 6.39854 6.28665 6.26632C6.41973 6.13409 6.589 6.04425 6.77309 6.00813C6.95718 5.97201 7.14784 5.99123 7.32102 6.06337C7.4942 6.13551 7.64212 6.25733 7.74613 6.41346C7.85014 6.56958 7.90556 6.75303 7.90542 6.94063C7.90719 7.06622 7.88365 7.19089 7.83621 7.3072C7.78878 7.42351 7.71841 7.52908 7.6293 7.61761C7.5402 7.70615 7.43418 7.77584 7.31757 7.82253C7.20096 7.86922 7.07614 7.89195 6.95056 7.88938ZM13.8744 13.8752H12.2214V11.0053C12.2214 10.1589 11.8616 9.89764 11.3972 9.89764C10.9067 9.89764 10.4255 10.2674 10.4255 11.0267V13.8752H8.77167V8.62118H10.3621V9.34917H10.3835C10.5431 9.02604 11.1023 8.47375 11.9556 8.47375C12.8783 8.47375 13.8752 9.02146 13.8752 10.6256L13.8744 13.8752Z"
      fill="white"
    />
  </svg>
);

const WebsiteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M1.25 10C1.25 5.175 5.175 1.25 10 1.25C14.825 1.25 18.75 5.175 18.75 10C18.75 14.825 14.825 18.75 10 18.75C5.175 18.75 1.25 14.825 1.25 10ZM2.52562 9.375H5.01938C5.08511 8.30218 5.32338 7.24698 5.725 6.25H3.50563C2.95082 7.20591 2.61604 8.27346 2.52562 9.375ZM6.27187 9.375H9.375V6.25H7.09125C6.62674 7.23182 6.34909 8.29155 6.2725 9.375M9.375 10.625H6.27187C6.34847 11.7085 6.62612 12.7682 7.09063 13.75H9.375V10.625ZM9.375 15H7.81625C8.25811 15.6352 8.78226 16.2089 9.375 16.7062V15ZM11.8781 17.2625C13.3107 16.8909 14.6014 16.1036 15.5875 15H13.6662C13.1828 15.8366 12.5808 16.5986 11.8787 17.2625M14.275 13.75H16.4937V13.7513C17.0489 12.795 17.384 11.727 17.4744 10.625H14.9806C14.9147 11.6979 14.6768 12.7531 14.275 13.75ZM13.7275 10.625H10.625V13.75H12.9087C13.3733 12.7682 13.6509 11.7085 13.7275 10.625ZM14.98 9.375H17.4738C17.3833 8.27298 17.0483 7.20498 16.4931 6.24875V6.25H14.2744C14.6762 7.24694 14.9141 8.30215 14.98 9.375ZM12.9087 6.25H10.625V9.375H13.7281C13.6515 8.29155 13.3739 7.23182 12.9094 6.25M13.6669 5H15.5875C14.6027 3.89775 13.3142 3.11107 11.8837 2.73875C12.5837 3.40261 13.1838 4.16425 13.6656 5M10.625 3.29375V5H12.1838C11.7419 4.36483 11.2177 3.79108 10.625 3.29375ZM9.375 3.29375C8.78226 3.79108 8.25811 4.36483 7.81625 5H9.375V3.29375ZM10.625 15V16.7062C11.2177 16.2089 11.7419 15.6352 12.1838 15H10.625ZM3.50563 13.75H5.72688C5.32461 12.7531 5.08571 11.6979 5.01938 10.625H2.525C2.61875 11.7563 2.96437 12.8162 3.505 13.75M8.125 17.2631C7.42295 16.5991 6.82086 15.8368 6.3375 15H4.4125C5.39956 16.1045 6.69159 16.892 8.12563 17.2631M6.33375 5C6.81596 4.16439 7.41656 3.40296 8.11688 2.73937C6.68652 3.11152 5.398 3.89798 4.41313 5H6.33375Z"
      fill="#83CBFF"
    />
  </svg>
);

const EmailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M17.9165 15.0007L12.3808 10.0007M7.61889 10.0007L2.0832 15.0007M1.6665 5.83398L8.4706 10.5969C9.02158 10.9825 9.29707 11.1754 9.59672 11.2501C9.86142 11.3161 10.1383 11.3161 10.403 11.2501C10.7026 11.1754 10.9781 10.9825 11.5291 10.5969L18.3332 5.83398M5.6665 16.6673H14.3332C15.7333 16.6673 16.4334 16.6673 16.9681 16.3948C17.4386 16.1552 17.821 15.7727 18.0607 15.3023C18.3332 14.7675 18.3332 14.0674 18.3332 12.6673V7.33398C18.3332 5.93385 18.3332 5.23379 18.0607 4.69901C17.821 4.2286 17.4386 3.84615 16.9681 3.60647C16.4334 3.33398 15.7333 3.33398 14.3332 3.33398H5.6665C4.26637 3.33398 3.56631 3.33398 3.03153 3.60647C2.56112 3.84615 2.17867 4.2286 1.93899 4.69901C1.6665 5.23379 1.6665 5.93385 1.6665 7.33398V12.6673C1.6665 14.0674 1.6665 14.7675 1.93899 15.3023C2.17867 15.7727 2.56112 16.1552 3.03153 16.3948C3.56631 16.6673 4.26637 16.6673 5.6665 16.6673Z"
      stroke="#343434"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SOCIAL_ICON_MAP = {
  linkedin_url: LinkedInIcon,
  twitter_url: TwitterIcon,
  facebook_url: FacebookIcon,
  website: WebsiteIcon,
  email: EmailIcon,
};

// Each link is an icon-only anchor, so it needs a text alternative (i18n key).
const SOCIAL_LINK_LABEL_KEYS = {
  linkedin_url: 'linkedin_profile',
  twitter_url: 'x_profile',
  facebook_url: 'facebook_profile',
  website: 'website',
  email: 'email',
};

// SB-5676: these hrefs come straight out of a speaker's profile fields, so the
// value is attacker-controllable by anyone who can edit a speaker record. A raw
// `javascript:` value used to be rendered verbatim and would execute in the
// embed's origin on click; a protocol-less one ("acme.com") resolved relative to
// the embed and went nowhere. normalizeUrl blocks the dangerous schemes and
// forces https on the rest. Returns null when the value cannot be made safe —
// the caller then drops the icon rather than rendering a dead or hostile link.
const getSocialHref = (fieldId, value) => {
  if (fieldId === 'email') {
    const email = String(value).trim();
    // Anything that is not a plain address (a `javascript:` payload, a CRLF
    // header injection) must not become a mailto target.
    // The pattern already excludes whitespace, quotes and angle brackets, so
    // the address needs no further encoding to be a safe mailto target.
    return /^[^\s@<>"'`?&]+@[^\s@<>"'`?&]+\.[^\s@<>"'`?&]+$/.test(email)
      ? `mailto:${email}`
      : null;
  }
  return normalizeUrl(value);
};

const SpeakerModal = ({
  speakerData,
  visibleSpeakerFields = [],
  visibleSpeakerFieldsById = {},
  onClose,
  onBack,
  onSessionClick,
  primaryColor,
  hideSessionDetails = false,
}) => {
  const { t } = useI18n();
  // A field earns an icon only when the embed selected it, the speaker has a
  // value, and that value survives getSocialHref (SB-5676).
  const socialLinks = SOCIAL_FIELD_IDS.map(fieldId => ({
    fieldId,
    href:
      visibleSpeakerFields.includes(fieldId) && speakerData[fieldId]
        ? getSocialHref(fieldId, speakerData[fieldId])
        : null,
  })).filter(link => link.href);

  // SB-8942: multi-value custom fields arrive as `<field>List` alongside the
  // comma-joined string, so a value containing a comma stays one item.
  const getFieldListValue = fieldId => {
    const list = speakerData[`${fieldId}List`];
    return Array.isArray(list) && list.length > 0 ? list.filter(Boolean) : null;
  };

  const customVisibleFields = visibleSpeakerFields.filter(
    fieldId =>
      !FIXED_SPEAKER_FIELD_IDS.includes(fieldId) &&
      !SOCIAL_FIELD_IDS.includes(fieldId) &&
      !NAME_FIELD_IDS.includes(fieldId) &&
      speakerData[fieldId] !== null &&
      speakerData[fieldId] !== undefined &&
      speakerData[fieldId] !== ''
  );

  // Group custom fields into pairs for 2-column layout
  const customFieldPairs = [];
  for (let i = 0; i < customVisibleFields.length; i += 2) {
    customFieldPairs.push(customVisibleFields.slice(i, i + 2));
  }

  return (
    <Modal
      onClose={onClose}
      onBack={onBack}
      className="speaker-modal"
      contentClassName="speaker-modal__content"
      style={{ '--color-primary': primaryColor }}
    >
      {/* Speaker Header */}
      <div className="speaker-modal__speaker-header">
        <div className="speaker-modal__speaker-profile">
          <div className="speaker-modal__avatar">
            <SpeakerHeadshot speaker={speakerData} height={100} width={100} />
          </div>

          <div className="speaker-modal__speaker-info">
            <div className="speaker-modal__speaker-details">
              <h1 className="speaker-modal__speaker-name">
                {getFormattedSpeakerName(
                  visibleSpeakerFields.includes('salutation') ? speakerData.salutation : '',
                  speakerData.full_name,
                  visibleSpeakerFields.includes('honorific') ? speakerData.honorific : ''
                )}
              </h1>
              {speakerData.title && <p className="speaker-modal__speaker-title">{speakerData.title}</p>}
              {speakerData.company && <p className="speaker-modal__speaker-company">{speakerData.company}</p>}
            </div>
            {socialLinks.length > 0 && (
              <div className="speaker-modal__social-links">
                {socialLinks.map(({ fieldId, href }) => {
                  const Icon = SOCIAL_ICON_MAP[fieldId];
                  return (
                    <a
                      key={fieldId}
                      href={href}
                      className="speaker-modal__social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t(SOCIAL_LINK_LABEL_KEYS[fieldId])}
                    >
                      <Icon />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* About Speaker */}
      <div className="speaker-modal__about">
        <ExpandableBio htmlContent={speakerData.about} maxLength={300} className="speaker-modal__bio" />

        {/* Visible Custom Fields */}
        {customFieldPairs.length > 0 && (
          <div className="speaker-modal__custom-fields">
            {customFieldPairs.map((pair, index) => (
              <div key={index} className="speaker-modal__detail-row">
                {pair.map(fieldId => (
                  <div key={fieldId} className="speaker-modal__detail-item">
                    <span className="speaker-modal__detail-label">
                      {visibleSpeakerFieldsById[fieldId]?.name || fieldId}:
                    </span>
                    {getFieldListValue(fieldId) ? (
                      <span className="speaker-modal__detail-value sb-dot-list">
                        {getFieldListValue(fieldId).map((value, i) => (
                          <span key={i} className="sb-dot-list__item">
                            {value}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <FieldTextValue html={speakerData[`${fieldId}Html`]} className="speaker-modal__detail-value">
                        {speakerData[fieldId]}
                      </FieldTextValue>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sessions — SB-6110: hidden when the speaker embed hides session details;
          the length guard also drops the empty "Sessions (0)" heading for
          genuinely session-less speakers. */}
      {!hideSessionDetails && speakerData.sessions?.length > 0 && (
      <div className="speaker-modal__sessions">
        <h2 className="speaker-modal__sessions-title">{t('sessions_heading', { count: speakerData.sessions.length })}</h2>

        <div className="speaker-modal__sessions-list">
          {speakerData.sessions.map(session => (
            <div
              key={session.id}
              className={`speaker-modal__session-card ${
                session.isHighlighted ? 'speaker-modal__session-card--highlighted' : ''
              }`}
              onClick={() => onSessionClick?.(session.id)}
            >
              <h3 className="speaker-modal__session-title">{session.title}</h3>
              <div className="speaker-modal__session-meta">
                {isDisplayableRange(session.range) ? (
                  <div className="speaker-modal__session-date">
                    <Clock size={14} />
                    <span>{session.range}</span>
                  </div>
                ) : null}
                {session.location && (
                  <div className="speaker-modal__session-location">
                    <MapPin size={14} />
                    <span>{session.location}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </Modal>
  );
};

export default SpeakerModal;
